import { Experiment } from './types/Experiment';
import { ExperimentData } from './types/ExperimentData';
import { ExperimentsCookie } from './types/ExperimentsCookie';
import { ExperimentVariant } from './types/ExperimentVariant';
import { ExperimentName, EXPERIMENTS } from './experiments';

import { CookieService } from '@/infrastructure/services/cookie';

const EXPERIMENTS_COOKIE_NAME = 'experiments';
const COOKIE_MAX_LIFE_TIME = 30 * 24 * 60 * 60;

const getRandomVariant = (variants: ExperimentVariant[]) => {
	const randomIndex = Math.floor(Math.random() * variants.length);

	return variants[randomIndex];
};

const tryParseExperiment = (jsonStr: string | undefined) => {
	if (!jsonStr) {
		return undefined;
	}

	try {
		return JSON.parse(jsonStr) as ExperimentData;
	} catch {
		return undefined;
	}
};

const loadExperiment = (name: string): Experiment | undefined => {
	const experimentData = tryParseExperiment(EXPERIMENTS[name]);

	if (!experimentData || typeof experimentData !== 'object') {
		return undefined;
	}

	const { expDate, variants } = experimentData;
	const validVariants = Array.isArray(variants) && variants.length > 0;

	if (!validVariants) {
		return undefined;
	}

	return {
		name: name as ExperimentName,
		expDate,
		variants,
	};
};

const loadActiveExperiments = (): Experiment[] =>
	Object.keys(EXPERIMENTS)
		.map(loadExperiment)
		.filter((experiment) => experiment !== undefined);

const writeExperiments = (experimentsMap: ExperimentsCookie): void => {
	const expires = Math.round(Date.now() / 1000) + COOKIE_MAX_LIFE_TIME;
	const value = encodeURIComponent(JSON.stringify(experimentsMap));

	CookieService.setCookie(EXPERIMENTS_COOKIE_NAME, value, expires);
};

const clearExperiments = (): void => {
	CookieService.deleteCookie(EXPERIMENTS_COOKIE_NAME);
};

const getExperiments = (): ExperimentsCookie | undefined => {
	const rawValue = CookieService.getCookie(EXPERIMENTS_COOKIE_NAME);
	if (!rawValue) {
		return undefined;
	}
	const decodedValue = decodeURIComponent(rawValue);
	try {
		return JSON.parse(decodedValue) as ExperimentsCookie;
	} catch {
		return undefined;
	}
};

const getFormattedActiveExperiments = (): string[] => {
	const experimentsCookie = getExperiments();
	if (!experimentsCookie) {
		return [];
	}

	return Object.entries(experimentsCookie)
		.filter(([name, variant]) => name && variant)
		.map(([name, variant]) => `${name}__${variant}`);
};

const init = (): void => {
	const storedExperiments = getExperiments();
	const activeExperiments = loadActiveExperiments();

	if (activeExperiments.length === 0) {
		clearExperiments();

		return;
	}

	const experimentsToStore: ExperimentsCookie = {};

	activeExperiments.forEach((exp) => {
		if (storedExperiments && exp.name in storedExperiments) {
			experimentsToStore[exp.name] = storedExperiments[exp.name];
		} else {
			experimentsToStore[exp.name] = getRandomVariant(exp.variants);
		}
	});

	writeExperiments(experimentsToStore);
};

export const abTests = {
	init,
	getExperiments,
	getFormattedActiveExperiments,
};
